import React, { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import listProcessedFluidRouteSessions from '../../../../repository/route-sessions/processed-fluids/listProcessedFluidRouteSessions';
import { useFetchRequiredSeals } from '../../../../data/generic-queries/processed-fluid-route-sessions';
import { filterRouteSessionsBySealRequirement } from '../../../../constants/logistics/procesed-fluids/processed-fluid-seals';
import Select from '../select/Select';

const resolveValue = (value) => {
    if (value === null || value === undefined) {
        return undefined;
    }
    if (typeof value === 'object') {
        return value._id || value.name || value;
    }
    return value;
};

const ProcessedFluidRouteSession = ({ name, props, autoPopulateFields, filterByDestinationProcessorId }) => {
    const lastPopulatedId = useRef(null);
    const { setValue, setError, clearErrors, updateSelectedValues, selectedValues } = useFormContext();
    const { data: requiredSealsData } = useFetchRequiredSeals();
    const requiredSeals = requiredSealsData?.required_seals ?? 0;

    const selectedRouteSession = selectedValues?.[name];
    const selectedId = selectedRouteSession?._id;

    const {
        data: allOptions = [],
        isLoading: loading,
        isError,
    } = useQuery({
        queryKey: ['processed-fluid-route-sessions', { withPickup: true, withoutDropoff: true, withoutInProgressDropoff: true }],
        queryFn: () =>
            listProcessedFluidRouteSessions({
                withPickup: true,
                withoutDropoff: true,
                withoutInProgressDropoff: true,
            }),
        select: (data) => (data?.error ? [] : data),
        staleTime: 0,
        refetchOnMount: 'always',
    });

    const options = useMemo(() => {
        let filtered = filterRouteSessionsBySealRequirement(allOptions, requiredSeals);
        if (filterByDestinationProcessorId) {
            filtered = filtered.filter((session) => session.destination_processor_id?._id === filterByDestinationProcessorId);
        }
        return filtered;
    }, [allOptions, requiredSeals, filterByDestinationProcessorId]);

    useEffect(() => {
        if (isError) {
            setError(name, { type: 'fetch options', message: 'Could not fetch Route Session options' });
        } else {
            clearErrors([name]);
        }
    }, [isError, name, setError, clearErrors]);

    useEffect(() => {
        if (!selectedRouteSession || !autoPopulateFields || !selectedId) {
            return;
        }

        // Prevent re-running for the same route session
        if (lastPopulatedId.current === selectedId) {
            return;
        }
        lastPopulatedId.current = selectedId;

        autoPopulateFields.forEach(({ from, to }) => {
            const rawValue = selectedRouteSession[from];
            const resolvedValue = resolveValue(rawValue);

            if (resolvedValue !== undefined) {
                setValue(to, resolvedValue, { shouldDirty: false });
                if (rawValue && typeof rawValue === 'object') {
                    updateSelectedValues(to, rawValue);
                }
            }
        });
    }, [selectedId, autoPopulateFields, setValue]);

    const formatLabel = (option, all) => {
        let labelOption = option;
        if (typeof option === 'string') {
            labelOption = all?.find((opt) => (opt.id || opt._id) === option);
        }

        if (!labelOption || !Object.keys(labelOption).length) {
            return '';
        }

        const commodityName = labelOption.commodity?.name || labelOption.commodity_type || 'N/A';
        return `LT Number: ${labelOption.lt_number} - Commodity: ${commodityName}`;
    };

    return <Select name={name} props={{ ...props, options }} loading={loading} formatLabel={formatLabel} />;
};

export default ProcessedFluidRouteSession;
